import Papa from 'papaparse';
import { useDeferredValue, useMemo, useRef } from 'react';
import { OverallBenchmarkingDataResponse } from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { getTableFilterTerms } from '@Components/FilterBar/filterbar-utils';
import {
  CellData,
  ColumnMetaDataMap,
  constructTableData,
  formatCellLabel,
  getColumnMeta,
  MISSING_VALUE_LABEL
} from '@Components/MetricsTable/metrics-table-utils';
import { MetricsTable, MetricsTableHandle } from '@Components/MetricsTable/MetricsTable';
import { Spinner } from '@Components/Spinner/Spinner';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Placement, Size } from '@Components/designConstants';
import {
  defaultIconFor,
  flatRowsToPerformanceData,
  makeColumnMetaData,
  makeDimensionColumnMetaData,
  makeDefaultGetDimensionCell
} from '../../utils/columnMetricSystem/adapters';
import { snakeCaseToCapitalized } from '../../utils/data';
import { download } from '../../utils/download-utils';
import {
  DROPDOWN_PRESET_OPTIONS_BY_CHANNEL,
  getMetricsForChannel,
  resolveDataConfig,
  ResolvedDataConfig,
  ResolvedDimConfig,
  resolveDimConfig
} from './globalBenchmarkingConstants';

// We only ever have one bucket of metric data per row in this page, so a single key is enough.
const FETCH_KEY = 'default';

interface ChannelGlobalBenchmarkingProps {
  channel: App2Channels;
  data: OverallBenchmarkingDataResponse | 'error' | undefined;
  isLoading: boolean;
  kpis: string[] | undefined;
  selectedKpi: string | undefined;
  setSelectedKpi: (kpi: string) => void;
  activePresetKey: string | undefined;
  setActivePresetKey: (key: string) => void;
  dateRange: DateRange;
  hasTableFilter: boolean;
  // Search terms are sent to the server; every change refetches.
  setTableFilterTerms: (terms: string[]) => void;
  // Used as the FilterBar `key` so its visible tokens reset when the parent
  // resets the search terms.
  filterResetKey: string;
}

export const ChannelGlobalBenchmarking = ({
  channel,
  data,
  isLoading,
  kpis,
  selectedKpi,
  setSelectedKpi,
  activePresetKey,
  setActivePresetKey,
  dateRange,
  hasTableFilter,
  setTableFilterTerms,
  filterResetKey
}: ChannelGlobalBenchmarkingProps) => {
  // Lazy: the table exposes a getter, we pull on Download click.
  const tableRef = useRef<MetricsTableHandle>(null);

  // Defer every input that feeds constructTableData so its heavy memo chain
  // runs in a background render, not the cheap high-pri one.
  const deferredData = useDeferredValue(data);
  const deferredChannel = useDeferredValue(channel);
  const deferredActivePresetKey = useDeferredValue(activePresetKey);
  const deferredKpis = useDeferredValue(kpis);

  const preset = useMemo(
    () => (deferredActivePresetKey ? deferredActivePresetKey.split(',') : undefined),
    [deferredActivePresetKey]
  );
  const hasPerformance = Boolean(deferredKpis && deferredKpis.length > 0);
  const channelMetrics = useMemo(
    () => getMetricsForChannel(deferredChannel, hasPerformance),
    [deferredChannel, hasPerformance]
  );

  const kpiOptions = useMemo(
    () =>
      (kpis ?? []).map(k => ({
        value: k,
        label: snakeCaseToCapitalized(k.replace(/-/g, '_'))
      })),
    [kpis]
  );

  const presetOptions = useMemo(() => DROPDOWN_PRESET_OPTIONS_BY_CHANNEL[channel] ?? [], [channel]);

  const dimensionConfigs = useMemo<ResolvedDimConfig[]>(() => {
    if (!preset) return [];
    return preset
      .map(name => resolveDimConfig(name, deferredChannel))
      .filter((cfg): cfg is ResolvedDimConfig => Boolean(cfg));
  }, [deferredChannel, preset]);

  const dataConfigs = useMemo<ResolvedDataConfig[]>(() => {
    if (!preset) return [];
    return channelMetrics
      .map(name => resolveDataConfig(name, deferredChannel))
      .filter((cfg): cfg is ResolvedDataConfig => Boolean(cfg));
  }, [deferredChannel, channelMetrics, preset]);

  const dimensionColumns = useMemo(
    () =>
      dimensionConfigs.map(cfg => ({
        id: `dim_${cfg.name}`,
        dimensionTypeName: cfg.prettyName,
        dimensionVarName: cfg.name,
        label: cfg.prettyName,
        icon: defaultIconFor(cfg.iconStyle)
      })) as DimensionColumn[],
    [dimensionConfigs]
  );

  const dataColumns = useMemo(
    () =>
      dataConfigs.map(cfg => ({
        id: `col_${cfg.name}`,
        dataVarName: cfg.name,
        label: cfg.prettyName,
        decimals: 2
      })) as Column[],
    [dataConfigs]
  );

  const rows = useMemo(
    () => (deferredData && deferredData !== 'error' ? deferredData.rows : []),
    [deferredData]
  );

  const filterBarOptions = useMemo(
    () => dimensionConfigs.map(cfg => ({ name: cfg.name, label: cfg.prettyName })),
    [dimensionConfigs]
  );

  const columnMetaDataMap = useMemo(
    () => ({
      ...makeColumnMetaData(dataConfigs),
      ...makeDimensionColumnMetaData(dimensionConfigs)
    }),
    [dataConfigs, dimensionConfigs]
  );

  const performanceData = useMemo(() => flatRowsToPerformanceData(rows), [rows]);

  const getDimensionCell = useMemo(
    () => makeDefaultGetDimensionCell(dimensionConfigs),
    [dimensionConfigs]
  );

  // Cells are server-computed metric values. constructTableData's aggregateData would sum
  // them (wrong for ratios), so the totals row comes from the server's totals instead.
  const { dimensionData, tableData } = useMemo(
    () =>
      constructTableData(
        columnMetaDataMap,
        dataColumns,
        dimensionColumns,
        getDimensionCell,
        () => FETCH_KEY,
        performanceData
      ),
    [columnMetaDataMap, dataColumns, dimensionColumns, getDimensionCell, performanceData]
  );

  const totals = deferredData && deferredData !== 'error' ? deferredData.totals : null;

  // Server totals, formatted like the cells; a null total (e.g. zero volume) shows as missing.
  const aggregateData = useMemo<CellData[]>(
    () =>
      totals
        ? dataColumns.map(column => {
            const meta = getColumnMeta(columnMetaDataMap, column);
            const value = totals[column.dataVarName] ?? null;
            return {
              label: formatCellLabel(value, meta, column.decimals ?? meta?.decimals),
              value: value ?? MISSING_VALUE_LABEL
            };
          })
        : [],
    [columnMetaDataMap, dataColumns, totals]
  );

  const hasRenderedData = Boolean(deferredData && deferredData !== 'error' && rows.length > 0);

  // Extends the spinner through the deferred catch-up render when the data
  // shape changed (no useful "previous table" to keep on screen).
  const isStructurePending =
    data !== deferredData ||
    channel !== deferredChannel ||
    activePresetKey !== deferredActivePresetKey ||
    kpis !== deferredKpis;

  const onDownload = () => {
    if (!hasRenderedData) return;
    // Strip the totals row the table appends when aggregateData is non-empty.
    let exportRows = tableRef.current?.getExportedData() ?? [];
    if (exportRows.length === 0) return;
    if (aggregateData.length > 0 && exportRows.length > 1) {
      exportRows = exportRows.slice(0, -1);
    }
    const csv = Papa.unparse(exportRows);
    const fileName = `global-benchmarking-${channel}-${dateRange.start}-${dateRange.end}.csv`;
    download(csv, fileName, 'text/csv');
  };

  return (
    <WidgetContainer
      header={
        // `key` remounts on tab change so visible tokens clear; the other two
        // props keep that remount from re-applying stale tokens or firing a
        // redundant onFilter.
        <FilterBar
          size={Size.LG}
          key={filterResetKey}
          useLocalStorage={false}
          filterOnModeChange={false}
          options={filterBarOptions}
          lines={rows}
          onFilter={(_filter, tokens) => setTableFilterTerms(getTableFilterTerms(tokens))}
        />
      }
      rightActions={
        <>
          {kpiOptions.length > 0 && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              label='Benchmarking KPI'
              options={kpiOptions}
              value={selectedKpi}
              onChange={setSelectedKpi}
            />
          )}
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Preset'
            options={presetOptions}
            value={activePresetKey}
            onChange={setActivePresetKey}
            menuAlign='end'
          />
          <DownloadDropdown
            menuOptions={['CSV']}
            onClickOptions={[onDownload]}
            disabled={!hasRenderedData || isStructurePending}
          />
        </>
      }
    >
      {renderBody({
        isLoading: isLoading || isStructurePending,
        data: deferredData,
        hasRenderedData,
        hasTableFilter,
        dataColumns,
        dimensionColumns,
        dimensionData,
        tableData,
        aggregateData,
        columnMetaDataMap,
        tableRef
      })}
    </WidgetContainer>
  );
};

interface RenderBodyArgs {
  isLoading: boolean;
  data: OverallBenchmarkingDataResponse | 'error' | undefined;
  hasRenderedData: boolean;
  hasTableFilter: boolean;
  dataColumns: Column[];
  dimensionColumns: DimensionColumn[];
  dimensionData: Record<string, CellData>[];
  tableData: CellData[][];
  aggregateData: CellData[];
  columnMetaDataMap: ColumnMetaDataMap;
  tableRef: React.RefObject<MetricsTableHandle | null>;
}

const renderBody = ({
  isLoading,
  data,
  hasRenderedData,
  hasTableFilter,
  dataColumns,
  dimensionColumns,
  dimensionData,
  tableData,
  aggregateData,
  columnMetaDataMap,
  tableRef
}: RenderBodyArgs) => {
  if (isLoading) {
    return (
      <div className='global-benchmarking-spinner-wrap'>
        <Spinner />
      </div>
    );
  }
  if (data === 'error') {
    return <div className='global-benchmarking-empty-message'>Error loading data. Try again.</div>;
  }
  if (data && data.rows.length === 0) {
    return (
      <div className='global-benchmarking-empty-message'>
        {hasTableFilter ? 'No rows match the current filter.' : 'No data for this view.'}
      </div>
    );
  }
  if (!hasRenderedData) {
    return undefined;
  }
  return (
    <MetricsTable
      ref={tableRef}
      disableMultiSorting
      dataColumns={dataColumns}
      dimensionColumns={dimensionColumns}
      dimensionData={dimensionData}
      tableData={tableData}
      aggregateData={aggregateData}
      columnMetaDataMap={columnMetaDataMap}
      fillParentHeight
    />
  );
};
